import {observer} from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import type {ICrossPortfolioLinkData} from "../../../../modules/space/spaceeditor/ui/actionbar/CrossPortfolioXyicon";
import type {Portfolio} from "../../../../../data/models/Portfolio";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import type {IXyiconLinkObject} from "../../../../../data/state/AppActions";
import type {Xyicon3D} from "../../../../modules/space/spaceeditor/logic3d/elements3d/Xyicon3D";
import {ReactUtils} from "../../../../utils/ReactUtils";
import type {Xyicon} from "../../../../../data/models/Xyicon";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import CloseIcon from "../../../icons/xmark-large.svg?react";
import {DraggableXyiconCatalogContainerV5} from "../../../dockable/DraggableXyiconCatalogContainerV5";
import {XyiconFeature} from "../../../../../generated/api/base";
import {colorPalette} from "../../../styles/colorPalette";
import {THREEUtils} from "../../../../../utils/THREEUtils";
import {baseDistance, fontSize, radius} from "../../../styles/styles";
import {FocusLoss} from "../../../../../utils/ui/focus/FocusLoss";
import {CrossPortfolioXyiconV5} from "./CrossPortfolioXyiconV5";

type InvisibleLinkedXyiconType = "embedded" | "external";

interface ICrossPortfolioLinkGroup {
	portfolio: Portfolio;
	data: ICrossPortfolioLinkData[];
}

interface ILinkedXyiconsWindowProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly objectAID: string;
	readonly type: InvisibleLinkedXyiconType;
	readonly onClose: () => void;
}

interface ILinkedXyiconsWindowState {
	isHidden: boolean;
}

@observer
export class LinkedXyiconsWindowV5 extends React.Component<ILinkedXyiconsWindowProps, ILinkedXyiconsWindowState> {
	private _ref = React.createRef<HTMLDivElement>();

	constructor(props: ILinkedXyiconsWindowProps) {
		super(props);
		this.state = {
			isHidden: false,
		};
	}

	private hide = () => {
		if (!this.state.isHidden) {
			this.setState({
				isHidden: true,
			});
		}
	};

	private getGroupedCrossPortfolioLinks(crossPortfolioLinks: ICrossPortfolioLinkData[]): ICrossPortfolioLinkGroup[] {
		// group by portfolioIds
		const groups: {
			[portfolioId: string]: ICrossPortfolioLinkData[];
		} = {};

		for (const link of crossPortfolioLinks) {
			if (!groups[link.otherPortfolioId]) {
				groups[link.otherPortfolioId] = [];
			}

			groups[link.otherPortfolioId].push(link);
		}

		const {actions} = this.props.spaceViewRenderer;

		const groupsArray: {
			portfolio: Portfolio;
			data: ICrossPortfolioLinkData[];
		}[] = [];

		for (const portfolioId in groups) {
			groupsArray.push({
				portfolio: actions.getPortfolioById(portfolioId),
				data: groups[portfolioId],
			});
		}

		return groupsArray.toSorted((a, b) => StringUtils.sortIgnoreCase(a.portfolio.name, b.portfolio.name));
	}

	private getCrossPortfolioLinkElements(crossPortfolioLinkGroups: ICrossPortfolioLinkGroup[]) {
		// make react nodes from data
		const reactNodes: React.ReactNode[] = [];

		for (const group of crossPortfolioLinkGroups) {
			reactNodes.push(
				<React.Fragment key={group.portfolio.id}>
					{this.getPortfolioNameElement(group.portfolio.name)}
					<div className="glyphContainer">
						{group.data.map((o) => (
							<CrossPortfolioXyiconV5
								key={o.link.id}
								transport={this.props.spaceViewRenderer.transport}
								linkData={o}
								showBreakLinkButton={true}
								showDeleteButton={true}
							/>
						))}
					</div>
				</React.Fragment>,
			);
		}

		return reactNodes;
	}

	private getInPortfolioLinks() {
		const linkedXyicons = this.props.spaceViewRenderer.actions.getLinksXyiconXyicon(this.props.objectAID);

		if (this.props.type === "embedded") {
			return linkedXyicons.filter((l) => l.link.isEmbedded);
		} // type === "external"
		else {
			return linkedXyicons.filter((l) => l.object.isExternal || l.object.isUnplotted);
		}
	}

	private getCrossPortfolioLinks() {
		return this.props.spaceViewRenderer.transport.appState.actions.getCrossPortfolioLinksXyiconXyicon(this.props.objectAID);
	}

	private getPortfolioNameElement(portfolioName: string) {
		return <div className="portfolioName">{portfolioName}</div>;
	}

	private areThereMoreThanOneInARow(inPortfolioLinks: IXyiconLinkObject[], crossPortfolioLinkGroups: ICrossPortfolioLinkGroup[]): boolean {
		if (inPortfolioLinks.length > 1) {
			return true;
		}

		return crossPortfolioLinkGroups.some((g) => g.data.length > 1);
	}

	public override componentDidMount() {
		const {signals} = this.props.spaceViewRenderer.toolManager.cameraControls;

		FocusLoss.listen(this._ref.current, this.props.onClose);

		signals.cameraPropsChange.add(this.props.onClose);
		// the initial position can be wrong (out of canvas), and it needs to be added to the dom in order to calculate its size. A refresh fixes this
		this.forceUpdate();
	}

	public override componentWillUnmount() {
		const {signals} = this.props.spaceViewRenderer.toolManager.cameraControls;

		FocusLoss.stopListen(this._ref.current, this.props.onClose);
		signals.cameraPropsChange.remove(this.props.onClose);
	}

	public override render() {
		const {spaceViewRenderer, objectAID, type} = this.props;
		const fromObject = spaceViewRenderer.xyiconManager.getItemById(objectAID) as Xyicon3D;

		if (!fromObject) {
			// This can happen, if parent xyicon gets deleted while this component is open
			requestAnimationFrame(this.props.onClose);
			return null;
		}
		const fromPosition = fromObject.position;
		const fromObjectScale = fromObject.scale;

		fromPosition.y += fromObjectScale.y / 2;

		const inPortfolioLinks = this.getInPortfolioLinks();
		const xyiconLinkObjects = this.getInPortfolioLinks().map((l) => ({object: l.object, userData: l.link}));
		const crossPortfolioLinks = this.props.type === "external" ? this.getCrossPortfolioLinks() : [];
		const crossPortfolioLinkGroups = this.getGroupedCrossPortfolioLinks(crossPortfolioLinks);

		const numberOfAllLinksToShow = xyiconLinkObjects.length + crossPortfolioLinks.length;
		const trailingSForInPortfolioLinksMaybe = `${xyiconLinkObjects.length === 1 ? "" : "s"}`;
		const trailingSForAllExternalLinksMaybe = `${crossPortfolioLinks.length === 1 ? "" : "s"}`;

		return (
			numberOfAllLinksToShow > 0 && (
				<LinkedXyiconsWindowStyled
					ref={this._ref}
					className={ReactUtils.cls("LinkedXyiconsWindow window vbox", {hidden: this.state.isHidden})}
					style={{
						width: this.areThereMoreThanOneInARow(inPortfolioLinks, crossPortfolioLinkGroups) ? "" : "270px", // derived from scss - "normal" width is 430px
						...THREEUtils.getStyleForFloatingUIElement(
							fromPosition.x,
							fromPosition.y,
							spaceViewRenderer.spaceOffset.z,
							spaceViewRenderer,
							true,
							"top",
							this._ref.current,
						),
					}}
				>
					<div className="header">
						<div className="title">
							{type === "embedded" ? (
								<>
									{`${xyiconLinkObjects.length} item${trailingSForInPortfolioLinksMaybe} embedded in `}
									<strong>{`${(fromObject.modelData as Xyicon).refId}`}</strong>
								</>
							) : (
								<>
									<strong>{`${(fromObject.modelData as Xyicon).refId}`}</strong>
									{` is linked to `}
									<strong>{numberOfAllLinksToShow}</strong>
									{` object${trailingSForAllExternalLinksMaybe} in other spaces`}
								</>
							)}
						</div>
						<IconButtonV5
							IconComponent={CloseIcon}
							onClick={this.props.onClose}
						/>
					</div>
					<XyiconContainer>
						{xyiconLinkObjects.length > 0 && (
							<div className="linkedXYiconCard">
								{this.props.type === "external" &&
									this.getPortfolioNameElement(
										`${this.props.spaceViewRenderer.transport.appState.actions.getCurrentPortfolioName()} (Current Portfolio)`,
									)}
								<div className="glyphContainer">
									<DraggableXyiconCatalogContainerV5
										spaceViewRenderer={spaceViewRenderer}
										feature={XyiconFeature.Xyicon}
										items={xyiconLinkObjects}
										onPointerMove={this.hide}
										onPointerUp={this.props.onClose}
									/>
								</div>
							</div>
						)}
						{crossPortfolioLinks.length > 0 && this.getCrossPortfolioLinkElements(crossPortfolioLinkGroups)}
					</XyiconContainer>
				</LinkedXyiconsWindowStyled>
			)
		);
	}
}

const LinkedXyiconsWindowStyled = styled.div`
	position: absolute;
	font-size: ${fontSize.md};
	background-color: ${colorPalette.white};
	z-index: 1;
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
	border-radius: ${radius.md};
	max-height: 500px;

	.linkedXYiconCard {
		margin: 10px 0;
	}

	.glyphContainer {
		display: grid;
		grid-template-columns: 248px 248px;
	}

	.header {
		min-height: 32px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: ${baseDistance.sm};
		padding: ${baseDistance.sm};

		svg {
			width: 16px;
			height: 16px;
		}
	}

	.DraggableXyiconCatalogItem {
		max-width: 244px;
		padding: ${baseDistance.xs} 0;
	}

	.portfolioName {
		color: ${colorPalette.gray.c700Dark};
	}
`;

const XyiconContainer = styled.div`
	padding: ${baseDistance.xs};
	border: 1px solid ${colorPalette.gray.c300};
	border-radius: ${radius.sm};
	margin: 0 ${baseDistance.sm} ${baseDistance.sm} ${baseDistance.sm};
	overflow-y: auto;

	.glyphContainer {
		overflow-y: initial;
	}
`;
